<template>
  <div class="flex flex-col h-full overflow-y-auto bg-white">
    <!-- Header -->
    <div class="px-4 py-3 border-b border-gray-200 flex items-center justify-between shrink-0">
      <div>
        <h3 class="sensitive text-sm font-semibold text-gray-900">
          {{ opportunityData.patient_last_name }}, {{ opportunityData.patient_first_name }}
        </h3>
        <div class="flex items-center space-x-2 mt-1">
          <span class="sensitive text-xs text-gray-500">{{ opportunityData.patient_identifier }}</span>
          <span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
            :class="programClass">
            {{ displayProgram }}
          </span>
          <span v-if="opportunityData.hcc_code"
            class="text-xs font-mono text-gray-600">
            HCC {{ opportunityData.hcc_code }}
          </span>
          <span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
            :class="statusClass">
            {{ opportunityData.opp_status }}
          </span>
        </div>
      </div>
      <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
        <FontAwesomeIcon :icon="['fas', 'xmark']" class="text-lg" />
      </button>
    </div>

    <!-- Summary Bar -->
    <div class="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center space-x-4 text-xs text-gray-600 shrink-0">
      <span>{{ tasks.length }} tiers</span>
      <span>{{ workItems.length }} codes submitted</span>
      <span>{{ opportunityData.days_in_pipeline }} days in pipeline</span>
    </div>

    <!-- Task Timeline -->
    <div class="px-4 py-3 border-b border-gray-200">
      <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Task Timeline</h4>
      <div v-if="tasks.length" class="space-y-2">
        <div
          v-for="task in tasks"
          :key="task.task_id"
          class="flex items-center justify-between px-3 py-2 rounded-md border"
          :class="task.is_stale ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'"
        >
          <div class="flex items-center space-x-3">
            <span class="text-xs font-semibold w-12" :class="tierColor(task.tier)">
              {{ task.tier }}
            </span>
            <span class="text-xs px-1.5 py-0.5 rounded font-medium"
              :class="chartStatusClass(task.chart_status)">
              {{ chartStatusLabel(task.chart_status) }}
            </span>
            <!-- Actioning coder only. The assigned_coder_entity_name fallback was
                 dropped 2026-08-03 with the retired task-owner column. -->
            <span v-if="task.coder_entity_name" class="sensitive text-xs text-gray-500">
              {{ task.coder_entity_name }}
            </span>
          </div>
          <div class="flex items-center space-x-3 text-xs">
            <span class="text-gray-500">{{ task.time_in_stage_days }}d</span>
            <span v-if="task.is_stale" class="text-red-600 font-semibold">STALE</span>
            <span v-if="task.code_count > 0" class="text-gray-500">{{ task.code_count }} codes</span>
          </div>
        </div>
      </div>
      <p v-else class="text-xs text-gray-400 italic">No tasks found</p>
    </div>

    <!-- Work Items -->
    <div class="px-4 py-3">
      <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Code Suggestions</h4>
      <div v-if="workItems.length">
        <table class="w-full text-xs">
          <thead>
            <tr class="text-left text-gray-500 border-b border-gray-200">
              <th class="pb-1 font-medium">ICD</th>
              <th class="pb-1 font-medium">HCC</th>
              <th class="pb-1 font-medium">Status</th>
              <th class="pb-1 font-medium">Coder</th>
              <th class="pb-1 font-medium">QA</th>
              <th class="pb-1 font-medium">Reviewer</th>
              <th class="pb-1 font-medium">Date</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="w in workItems"
              :key="w.work_id"
              class="border-b border-gray-100"
            >
              <td class="py-1.5 font-mono">{{ w.icd_code }}</td>
              <td class="py-1.5 font-mono text-gray-500">{{ w.hcc_code || '\u2014' }}</td>
              <td class="py-1.5 text-gray-600">{{ w.icd_status || '\u2014' }}</td>
              <td class="sensitive py-1.5 text-gray-600">{{ w.coder_entity_name || '\u2014' }}</td>
              <td class="py-1.5">
                <span v-if="w.qa_concurrence === true" class="text-green-600 font-medium">Accept</span>
                <span v-else-if="w.qa_concurrence === false" class="text-red-600 font-medium">Reject</span>
                <span v-else class="text-gray-400">Pending</span>
              </td>
              <td class="sensitive py-1.5 text-gray-500">{{ w.qa_reviewer_entity_name || '\u2014' }}</td>
              <td class="py-1.5 text-gray-500">{{ formatDate(w.work_created_at) }}</td>
            </tr>
          </tbody>
        </table>

        <!-- QA Disagree Reasons (if any) -->
        <div v-for="w in rejectedItems" :key="'reason-' + w.work_id"
          class="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-xs">
          <span class="font-medium text-red-700">{{ w.icd_code }} rejected:</span>
          <DemoText tag="span" class="text-red-600 ml-1" :text="w.qa_disagree_reason" :record="opportunityData.patient_identifier" />
        </div>
      </div>
      <p v-else class="text-xs text-gray-400 italic">No code suggestions</p>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import DemoText from '@/components/DemoMode/DemoText.vue';

export default {
  name: 'Coding_Ops_Detail_Panel',
  components: { DemoText, FontAwesomeIcon },
  props: {
    opportunityData: { type: Object, required: true },
    tasks: { type: Array, default: () => [] },
    workItems: { type: Array, default: () => [] },
    // Optional display-label map for backend PROGRAM values. V2 passes
    // PROGRAM_LABELS for the Vytalize rename; V1 leaves it empty so the raw
    // backend value renders unchanged.
    programLabels: { type: Object, default: () => ({}) },
  },
  emits: ['close'],
  computed: {
    displayProgram() {
      const p = this.opportunityData.program;
      return this.programLabels[p] || p;
    },
    programClass() {
      const p = this.opportunityData.program;
      if (p === 'Suspect') return 'bg-blue-100 text-blue-700';
      if (p === 'Resub') return 'bg-orange-100 text-orange-700';
      if (p === 'Early Start') return 'bg-purple-100 text-purple-700';
      return 'bg-gray-100 text-gray-700';
    },
    statusClass() {
      const s = this.opportunityData.opp_status;
      if (s === 'active') return 'bg-amber-100 text-amber-700';
      if (s === 'completed') return 'bg-green-100 text-green-700';
      return 'bg-gray-100 text-gray-500';
    },
    rejectedItems() {
      return this.workItems.filter(
        w => w.qa_concurrence === false && w.qa_disagree_reason
      );
    },
  },
  methods: {
    tierColor(tier) {
      if (tier === '1LR') return 'text-blue-600';
      if (tier === '2LR') return 'text-indigo-600';
      if (tier === '3LR') return 'text-purple-600';
      if (tier === 'Confirm') return 'text-green-600';
      return 'text-gray-600';
    },
    chartStatusClass(status) {
      if (status === 'completed') return 'bg-green-100 text-green-700';
      if (status === 'in_progress') return 'bg-amber-100 text-amber-700';
      return 'bg-gray-100 text-gray-500';
    },
    chartStatusLabel(status) {
      const map = { no_work: 'No Work', in_progress: 'In Progress', completed: 'Completed' };
      return map[status] || status || '\u2014';
    },
    formatDate(val) {
      if (!val) return '\u2014';
      return new Date(val).toLocaleDateString();
    },
  },
};
</script>
